<template>
  <div
    class="modal fade"
    id="adjustmentDetailsModal"
    tabindex="-1"
    aria-labelledby="adjustmentDetailsModalLabel"
    aria-hidden="true"
  >
    <div
      class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
    >
        <div class="modal-content" v-if="adjustment || loading">
        <div class="modal-header border-bottom-dashed">
          <h5 class="modal-title text-title fw-bold" id="adjustmentDetailsModalLabel">
            {{ t("adjustment_page.adjustment_details") }}{{ adjustment?.ref ? `: ${adjustment.ref}` : "" }}
          </h5>
          <button
            type="button"
            class="btn-close p-0 shadow-none"
            data-bs-dismiss="modal"
            :aria-label="t('common.close')"
          >
            <i class="ri-close-line fs-24"></i>
          </button>
        </div>
        <div class="modal-body" v-if="loading">
          <div class="d-flex justify-content-center align-items-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ t("loading") }}</span>
            </div>
          </div>
        </div>
        <div class="modal-body" v-else-if="adjustment">
          <div class="row mb-4">
            <div class="col-md-4">
              <p class="mb-1 text-muted fs-13">{{ t("adjustment_page.date_label") }}</p>
              <p class="fw-semibold text-dark">{{ formatDate(adjustment.created_at) }}</p>
            </div>
            <div class="col-md-4">
              <p class="mb-1 text-muted fs-13">{{ t("adjustment_page.reference_label") }}</p>
              <p class="fw-semibold text-dark">{{ adjustment.ref }}</p>
            </div>
            <div class="col-md-4">
              <p class="mb-1 text-muted fs-13">{{ t("adjustment_page.warehouse_label") }}</p>
              <p class="fw-semibold text-dark">{{ adjustment.warehouse_name }}</p>
            </div>
          </div>

          <h6 class="fw-bold mb-3">{{ t("adjustment_page.adjusted_products") }}</h6>
          <div class="table-responsive style-two">
            <table class="table text-nowrap align-middle mb-0">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="text-title fw-semibold fs-13 lh-1">{{ t("adjustment_page.no") }}</th>
                  <th scope="col" class="text-title fw-semibold fs-13 lh-1">{{ t("adjustment_page.product") }}</th>
                  <th scope="col" class="text-title fw-semibold fs-13 lh-1">{{ t("adjustment_page.code") }}</th>
                  <th scope="col" class="text-title fw-semibold fs-13 lh-1">{{ t("adjustment_page.quantity") }}</th>
                  <th scope="col" class="text-title fw-semibold fs-13 lh-1">{{ t("adjustment_page.type") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in adjustment.items" :key="index">
                  <td class="shadow-none lh-1 fs-14 fw-normal text-paragraph">
                    {{ Number(index) + 1 }}.
                  </td>
                  <td class="shadow-none lh-1 fs-14 fw-normal text-paragraph">
                    {{ item.product?.name || t("adjustment_page.not_available") }}
                  </td>
                  <td class="shadow-none lh-1 fs-14 fw-normal text-paragraph">
                    {{ item.product?.code || t("adjustment_page.not_available") }}
                  </td>
                  <td class="shadow-none lh-1 fs-14 fw-normal text-paragraph">
                    {{ item.quantity }}
                  </td>
                  <td class="shadow-none lh-1 fs-14 fw-normal">
                    <span :class="item.type === 'add' ? 'text-success' : 'text-danger'">
                      {{ item.type === 'add' ? t("adjustment_page.addition") : t("adjustment_page.subtraction") }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="mt-4" v-if="adjustment.note">
            <h6 class="fw-bold mb-2">{{ t("adjustment_page.note_label") }}</h6>
            <p class="text-paragraph fs-14 bg-light p-3 rounded">{{ adjustment.note }}</p>
          </div>
        </div>
        <div class="modal-footer border-top-dashed">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ t("common.close") }}</button>
          <button type="button" class="btn btn-primary" @click="handlePrint" :disabled="loading || !adjustment">
            <i class="ri-printer-line me-1"></i> {{ t("adjustment_page.print") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "DetailsModal",
  props: {
    adjustment: {
      type: Object as PropType<any>,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { t, locale } = useI18n();

    const formatDate = (dateString?: string) => {
      if (!dateString) return t("adjustment_page.not_available");
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const handlePrint = () => {
      window.print();
    };

    return {
      formatDate,
      handlePrint,
      t,
    };
  },
});
</script>
